/**
 * State management for the shared-list web fallback.
 *
 * Reuses `@carn/core`'s `ListItem`/`isChecked`/`makeItem`/`shoppingTripComplete`
 * directly — this file does not reimplement list logic, because the whole point of that
 * module is that the merge is commutative and lives in one place. `shoppingModeGroups`
 * (department grouping) is deliberately NOT used, and cannot be: RULINGS **R58** is
 * CONFIRMED ("the web fallback stays a flat add-and-check list ... Do not add department
 * grouping. Items stay oldest-first"), and the R48 wire does not even carry a department
 * (see `backend.ts`).
 *
 * The backend is chosen from the URL by `makeSharedListBackend` (RULINGS R48): the
 * capability-URL client when a token is present, the local preview when it is not. The
 * `status` this hook returns is what `SharedListPage` renders so the page states which
 * one it is running.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  checkItem,
  isChecked,
  makeItem,
  shoppingTripComplete,
  uncheckItem,
  type ListItem,
} from '@carn/core';
import {
  makeSharedListBackend,
  PENDING_LIST_ID,
  type LoadedList,
  type SharedListNotice,
  type SharedListStatus,
} from './backend';
import { getGuestId } from './guestId';

export type SharedListState = {
  loaded: boolean;
  status: SharedListStatus;
  /** The list's own name, when the server sent one. Never invented locally. */
  listName: string | undefined;
  unchecked: ListItem[];
  checked: ListItem[];
  complete: boolean;
  /**
   * Returns whether the item was ACCEPTED. False when the link turned out to be dead — the
   * caller must not announce "{item} added." for a write that went nowhere, which is the
   * whole content of RULINGS R68's second half.
   */
  addItem: (name: string) => Promise<boolean>;
  setChecked: (itemId: string, value: boolean) => Promise<void>;
  guestId: string;
  /** The most recent thing the page must SAY, as opposed to a state it is in. */
  notice: SharedListNotice | undefined;
};

export function useSharedList(args: {
  /** The capability token from the URL, or null for the local preview. */
  token: string | null;
  /** The list id the preview store writes under. Ignored when a token is present. */
  previewListId: string;
}): SharedListState {
  const { token, previewListId } = args;
  const [items, setItems] = useState<ListItem[]>([]);
  const [listId, setListId] = useState(previewListId);
  const [listName, setListName] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<SharedListStatus>(token ? 'loading' : 'preview');
  const [notice, setNotice] = useState<SharedListNotice | undefined>(undefined);
  const guestId = useMemo(getGuestId, []);

  // Refs so the callbacks given to the backend never go stale and never force the backend to
  // be rebuilt (which would restart the poll and re-read the list).
  const statusRef = useRef(setStatus);
  statusRef.current = setStatus;
  const noticeRef = useRef(setNotice);
  noticeRef.current = setNotice;
  // The status as of RIGHT NOW, readable from inside an async callback whose `status` closure
  // is a render old. A link can be revoked between the submit and the response, and `status`
  // would still read `'live'` at the point the announcement is decided.
  const statusNow = useRef<SharedListStatus>(token ? 'loading' : 'preview');
  statusNow.current = status;

  const { backend } = useMemo(
    () =>
      makeSharedListBackend({
        token,
        previewListId,
        onStatus: (next) => {
          statusNow.current = next;
          statusRef.current(next);
        },
        onNotice: (next) => noticeRef.current(next),
      }),
    [token, previewListId],
  );

  const absorb = useCallback((list: LoadedList) => {
    setItems(list.items);
    if (list.listId !== PENDING_LIST_ID) setListId(list.listId);
    if (list.name !== undefined) setListName(list.name);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    void backend.load().then((list) => {
      if (!alive) return;
      absorb(list);
      setLoaded(true);
    });
    const unsubscribe = backend.subscribe((list) => {
      if (alive) absorb(list);
    });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [backend, absorb]);

  const live = useMemo(
    () => items.filter((item) => item.removedAt === undefined).sort((a, b) => a.addedAt - b.addedAt),
    [items],
  );
  const unchecked = useMemo(() => live.filter((item) => !isChecked(item)), [live]);
  const checked = useMemo(() => live.filter((item) => isChecked(item)), [live]);
  const complete = useMemo(() => shoppingTripComplete(live), [live]);

  // A revoked link accepts nothing, and the surface must not flash an optimistic row that
  // the next read removes. The backend refuses too (`ShareLinkBackend.dead`) — this is the
  // half that stops the page from ever having claimed it (RULINGS R68).
  const dead = status === 'revoked';

  const addItem = useCallback(
    async (name: string): Promise<boolean> => {
      const trimmed = name.trim();
      if (!trimmed || dead) return false;
      const item = makeItem({
        id: crypto.randomUUID(),
        listId,
        name: trimmed,
        now: Date.now(),
        addedBy: guestId,
      });
      // Optimistic, then written. The local write happens first inside every backend
      // (implementation-brief §2), so this only mirrors what is already durable.
      setItems((prev) => [...prev, item]);
      await backend.addItem(item);
      // Re-read so the server's view — including anything the household added in the
      // meantime — replaces the optimistic guess rather than sitting alongside it.
      absorb(await backend.load());
      // The link may have been revoked between the submit and now, in which case the item was
      // refused and not queued. Reported to the caller so nothing announces an acceptance
      // that did not happen.
      return statusNow.current !== 'revoked';
    },
    [backend, listId, guestId, absorb, dead],
  );

  const setChecked = useCallback(
    async (itemId: string, value: boolean) => {
      if (dead) return;
      const now = Date.now();
      // THROUGH CORE, NOT BY HAND. This used to build `{...item, check: {at: now, by}}`
      // inline, which skips `checkItem`/`uncheckItem`'s `Math.max(now, other.at + 1)` rule
      // (`@carn/core`'s `lists.ts`) — so a check and an uncheck inside the same millisecond
      // rendered as still-checked while the server resolved it as unchecked. It self-healed
      // on the immediate re-read, so it was a render-only divergence; it was still a
      // hand-rolled copy of a rule the module header says lives in exactly one place.
      setItems((prev) =>
        prev.map((item) =>
          item.id !== itemId
            ? item
            : value
              ? checkItem(item, now, guestId)
              : uncheckItem(item, now, guestId),
        ),
      );
      await backend.setChecked(itemId, value, guestId);
      absorb(await backend.load());
    },
    [backend, guestId, absorb, dead],
  );

  return {
    loaded,
    status,
    listName,
    unchecked,
    checked,
    complete,
    addItem,
    setChecked,
    guestId,
    notice,
  };
}
